import { Component } from '@angular/core';
import { BackButton } from '../../shared/buttons/back-button';
import { Popup } from '../../../core/shared/components/popup/popup';
import { Table } from '../../shared/table/table';
import { CommonModule } from '@angular/common';
import { AppIcons } from '../../../core/icons/fa-icons';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { OnboradingService } from '../shared/service/onboarding.service';
import { Setup } from '../../setup/shared/service/setup';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { LeadService } from '../../leads/shared/service/leads.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { applyDesignationFieldState, buildCommitteePersonFields } from '../shared/committee-person-fields';

@Component({
  selector: 'app-key-management',
  imports: [CommonModule, Table, Popup, BackButton, TranslatePipe],
  templateUrl: './key-management.html',
  styleUrl: './key-management.css',
})
export class KeyManagement {
  icons = AppIcons;

  // ================= ROUTE PARAMS =================
  crptOnbrdngSeq: number | null = null;
  clntSeq: number | null = null;
  isViewMode = false;
  // ================= KEY_MANAGEMENT_PERSON_TYPE =================
  readonly KEY_MANAGEMENT_PERSON_TYPE = 3;
  readonly LIFETIME_CNIC_DATE = '3000-01-01';

  // ================= CNIC / PASSPORT DOCUMENT =================
  private readonly CNIC_PASSPORT_DOC_TYP = 39;
  private readonly CNIC_PASSPORT_ENTY_TYP = 16;

  readonly viewIcon = faEye;

  isPopupViewMode = false;

  get isRelationshipOfficer(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem('user_role') || '').trim().toUpperCase() === 'LNLY_RO'
    );
  }
  // ================= TABLE =================
  list: any[] = [];
  columns: TableColumn[] = [];

  // ================= POPUP =================
  showPopup = false;
  isEditMode = false;
  formData: any = {};
  fields: DynamicFieldConfig[] = [];

  // ================= DELETE KEY MANAGEMENT POPUP =================
  showDeletePopup = false;
  deleteMessage = '';
  private keyManagementToDelete: any = null;

  // ================= DROPDOWN OPTIONS =================
  countryOptions: any[] = [];
  nationalityOptions: any[] = [];
  departmentOptions: any[] = [];
  designationOptions: any[] = [];
  qualificationOptions: any[] = [];

  // ================= TRACK CASCADE =================
  private lastCntrySeq: number = 0;
  private lastStSeq: number = 0;
  private lastDstrctSeq: number = 0;
  private lastThslSeq: number = 0;
  private lastNtnltySeq: number = 0;
  private lastDsgntnSeq = 0;

  // ================= NATIONALITY BASED CNIC/PASSPORT VALIDATION =================
  private readonly PAKISTANI_REFCD_SEQ = 2685;
  private readonly CNIC_FIELD_KEYS = ['cnc', 'cncIssuDt', 'cncExpryDt', 'isCnicLifetime'];
  private readonly PASSPORT_FIELD_KEYS = ['psprt', 'psprtIssuDt', 'psprtExpryDt'];

  constructor(
    private onboarding: OnboradingService,
    private setup: Setup,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private pickListService: PickListService,
    private leadService: LeadService,
    private translate: TranslateService,
  ) { }

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) this.crptOnbrdngSeq = +id;
    });

    this.route.queryParamMap.subscribe((params) => {
      const clientSeq = params.get('clientSeq');
      if (clientSeq) this.clntSeq = +clientSeq;
      this.isViewMode = params.get('mode') === 'view';
      this.buildActions();
    });

    this.buildColumns();
    this.loadCountries();
    this.loadNationalities();
    this.loadDepartments();
    this.loadDesignations();
    this.loadQualifications();

    if (this.crptOnbrdngSeq) {
      this.loadData();
    }
  }

  // ================= LOAD NATIONALITIES =================
  loadNationalities(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.NATIONALITY]).subscribe(() => {
      this.nationalityOptions = this.pickListService.getOptionsSync(PKL_GRP.NATIONALITY);
      this.updateFieldOptions('ntnlty', this.nationalityOptions);
    });
  }

  // ================= LOAD DEPARTMENTS =================
  loadDepartments(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.DEPARTMENT]).subscribe(() => {
      this.departmentOptions = this.pickListService.getOptionsSync(PKL_GRP.DEPARTMENT);
      this.updateFieldOptions('dptNm', this.departmentOptions);
    });
  }

  // ================= LOAD DESIGNATIONS =================
  loadDesignations(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.DESIGNATION]).subscribe(() => {
      this.designationOptions = this.pickListService.getOptionsSync(PKL_GRP.DESIGNATION);
      this.updateFieldOptions('dsgntn', this.designationOptions);
    });
  }

  // ================= LOAD QUALIFICATIONS =================
  loadQualifications(): void {
    this.pickListService.loadCodesAsync([PKL_GRP.QUALIFICATION]).subscribe(() => {
      this.qualificationOptions = this.pickListService.getOptionsSync(PKL_GRP.QUALIFICATION);
      this.updateFieldOptions('qlfctn', this.qualificationOptions);
    });
  }
  private updatePopupFieldsReadonlyState(): void {
    const isLifetime = Boolean(this.formData?.isCnicLifetime);

    this.fields = this.fields.map((field) => ({
      ...field,
      disabled:
        this.isPopupViewMode ||
        (field.key === 'cncExpryDt' && isLifetime),
    }));
  }
  // ================= LOAD COUNTRIES =================
  loadCountries() {
    this.setup.getCountries().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((c: any) => ({
          label: c.cntryNm,
          value: c.countrySeq,
        }));

        this.countryOptions = options;

        this.buildFields();
      },
      error: () => {
        this.toastr.error(this.translate.instant('ONBOARDING.FAILED_TO_LOAD_COUNTRIES'));
        this.buildFields();
      },
    });
  }
  buildFields() {
    this.fields = buildCommitteePersonFields({
      nationalityOptions: this.nationalityOptions,
      departmentOptions: this.departmentOptions,
      designationOptions: this.designationOptions,
      qualificationOptions: this.qualificationOptions,
      countryOptions: this.countryOptions,
      isCnicLifetime: Boolean(this.formData?.isCnicLifetime),
    });
    this.fields = applyDesignationFieldState(this.fields, this.formData?.dsgntn);
  }
  // ================= GEO LOADERS =================
  loadStates(cntrySeq: number) {
    if (!cntrySeq) {
      this.updateFieldOptions('stSeq', []);
      return;
    }
    this.setup.getStates(cntrySeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((s: any) => ({
          label: s.stNm ?? s.name,
          value: s.stSeq ?? s.id,
        }));
        this.updateFieldOptions('stSeq', options);
      },
      error: () => this.updateFieldOptions('stSeq', []),
    });
  }

  loadDistricts(stSeq: number) {
    if (!stSeq) {
      this.updateFieldOptions('dstrctSeq', []);
      return;
    }
    this.setup.getDistricts(stSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((d: any) => ({
          label: d.dstrctNm ?? d.name,
          value: d.dstrctSeq ?? d.id,
        }));
        this.updateFieldOptions('dstrctSeq', options);
      },
      error: () => this.updateFieldOptions('dstrctSeq', []),
    });
  }

  loadTehsils(dstrctSeq: number) {
    if (!dstrctSeq) {
      this.updateFieldOptions('thslSeq', []);
      return;
    }
    this.setup.getTehsils(dstrctSeq).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((t: any) => ({
          label: t.thslNm ?? t.name,
          value: t.thslSeq ?? t.id,
        }));
        this.updateFieldOptions('thslSeq', options);
      },
      error: () => this.updateFieldOptions('thslSeq', []),
    });
  }

  loadCities(thslSeq?: number) {
    this.setup.getCity().subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const options = data.map((c: any) => ({
          label: c.ctyNm ?? c.cityNm ?? c.name,
          value: c.citySeq ?? c.cityId,
        }));
        this.updateFieldOptions('citySeq', options);
      },
      error: () => this.updateFieldOptions('citySeq', []),
    });
  }

  // ================= UPDATE FIELD OPTIONS =================
  updateFieldOptions(key: string, options: any[]) {
    const field = this.fields.find((f) => f.key === key);
    if (field) {
      field.options$ = options;
      this.fields = [...this.fields]; // trigger ngOnChanges in dynamic form
    }
  }

  // ================= NATIONALITY BASED CNIC/PASSPORT VALIDATION =================
  private isPakistanNationality(ntnltySeq: any): boolean {
    if (!ntnltySeq) {
      return false;
    }

    return Number(ntnltySeq) === this.PAKISTANI_REFCD_SEQ;
  }

  private updateNationalityFieldState(isPakistan: boolean): void {
    this.fields = this.fields.map((field) => {
      if (this.CNIC_FIELD_KEYS.includes(field.key)) {
        return {
          ...field,
          validations: { ...field.validations, required: isPakistan },
        };
      }

      if (this.PASSPORT_FIELD_KEYS.includes(field.key)) {
        return {
          ...field,
          validations: { ...field.validations, required: !isPakistan },
        };
      }

      return field;
    });
  }

  // ================= DOCUMENT HELPERS =================
  private static readonly DOC_EXT_BY_MIME: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  };

  /** Converts the file-upload field's data URL (base64) back into a binary File for the multipart payload. */
  private dataUrlToFile(dataUrl: any, fileName: string): File | null {
    const match = /^data:(.+?);base64,(.*)$/.exec(String(dataUrl ?? ''));

    if (!match) {
      return null;
    }

    const [, mimeType, base64] = match;
    const cleanBase64 = base64.replace(/\s/g, '');

    if (!cleanBase64) {
      return null;
    }

    const byteCharacters = atob(cleanBase64);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let index = 0; index < byteCharacters.length; index++) {
      byteArray[index] = byteCharacters.charCodeAt(index);
    }

    const extension = KeyManagement.DOC_EXT_BY_MIME[mimeType] || 'bin';

    return new File([byteArray], `${fileName}.${extension}`, { type: mimeType });
  }

  /** Guesses a stored document's mime type from its base64 signature (pdf / doc / docx only). */
  private detectDocMimeType(base64: string): string {
    const clean = base64.replace(/\s/g, '');

    if (clean.startsWith('JVBERi0')) {
      return 'application/pdf';
    }

    if (clean.startsWith('0M8R4KGxGuE')) {
      return 'application/msword';
    }

    if (clean.startsWith('UEsDB') || clean.startsWith('UEsBA') || clean.startsWith('UEsFB')) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }

    return 'application/octet-stream';
  }

  /** GET /leads/get-lead-docs-list/{leadSeq}/{entyTyp} — loads the person's uploaded CNIC/Passport doc for the edit/view popup. */
  private loadCnicPassportDoc(prsnSeq: number): void {
    if (!prsnSeq) {
      return;
    }

    this.leadService.getLeadDocs(prsnSeq, this.CNIC_PASSPORT_ENTY_TYP).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

        const doc = list.find(
          (item: any) => Number(item.docTyp) === this.CNIC_PASSPORT_DOC_TYP && !item.delFlg,
        );

        if (!doc?.docImg) {
          return;
        }

        const base64 = String(doc.docImg).trim();
        const mimeType = this.detectDocMimeType(base64);

        this.formData = {
          ...this.formData,
          cnicPassportDoc: `data:${mimeType};base64,${base64}`,
        };

        // Force DynamicFormComponent to rebuild from the updated model —
        // patching `formData` alone isn't reliably picked up (see
        // updateFieldOptions/updateNationalityFieldState above).
        this.fields = [...this.fields];
      },
      error: () => {
        this.toastr.error(this.translate.instant('ONBOARDING.FAILED_TO_LOAD_CNIC_PASSPORT_DOC'));
      },
    });
  }

  // ================= CNIC LIFETIME =================
  private isLifetimeCnicDate(value: any): boolean {
    if (!value) {
      return false;
    }

    return String(value).slice(0, 10) === this.LIFETIME_CNIC_DATE;
  }

  private updateCnicExpiryFieldState(isLifetime: boolean): void {
    this.formData = {
      ...this.formData,
      isCnicLifetime: isLifetime,
      ...(isLifetime ? { cncExpryDt: null } : {}),
    };

    this.updatePopupFieldsReadonlyState();
  }



  // ================= TABLE COLUMNS =================
  buildColumns() {
    this.columns = [
      { key: 'flNm', label: 'ONB_PERSON.FULL_NAME' },
      { key: 'cncOrPsprt', label: 'ONB_PERSON.CNIC_PASSPORT' },
      { key: 'cncOrPsprtIssuDt', label: 'ONB_PERSON.ISSUE_DATE' },
      { key: 'dob', label: 'ONB_PERSON.DOB' },
      // { key: 'shrhldng', label: 'ONB_PERSON.SHAREHOLDING_PERCENT' },
    ];
  }

  // ================= LOAD DATA =================
  loadData() {
    if (!this.crptOnbrdngSeq) return;

    this.onboarding.getCorporatePersons(this.crptOnbrdngSeq, this.KEY_MANAGEMENT_PERSON_TYPE).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : res?.data || [];

        this.list = data.map((item: any) => ({
          ...item,
          cncOrPsprt: item.cnc || item.psprt,
          cncOrPsprtIssuDt: item.cnc ? item.cncIssuDt : item.psprtIssuDt,
        }));
      },
      error: () => {
        this.list = [];
        this.toastr.error(this.translate.instant('ONB_KEY_MGMT.FAILED_TO_LOAD_KEY_MANAGEMENT'));
      },
    });
  }
  private capitalizeWords(value: any): any {
    if (typeof value !== 'string' || !value) {
      return value;
    }

    return value.replace(/(^|\s)(\S)/g, (_, space, char) => space + char.toUpperCase());
  }

  onPopupFormChange(form: any): void {
    if (!form || this.isPopupViewMode) {
      return;
    }

    const previousLifetimeValue = Boolean(
      this.formData?.isCnicLifetime,
    );

    this.formData = {
      ...this.formData,
      ...form,
      flNm: this.capitalizeWords(form.flNm),
      fthrHsbndNm: this.capitalizeWords(form.fthrHsbndNm),
    };

    const isCnicLifetime = Boolean(
      this.formData.isCnicLifetime,
    );

    if (isCnicLifetime !== previousLifetimeValue) {
      this.updateCnicExpiryFieldState(isCnicLifetime);
    }

    const ntnltySeq = Number(form.ntnlty) || 0;

    if (ntnltySeq !== this.lastNtnltySeq) {
      this.lastNtnltySeq = ntnltySeq;
      this.updateNationalityFieldState(this.isPakistanNationality(ntnltySeq));
    }

    // CEO: Name of Department and Head Of Department are optional.
    const dsgntnSeq = Number(form.dsgntn) || 0;

    if (dsgntnSeq !== this.lastDsgntnSeq) {
      this.lastDsgntnSeq = dsgntnSeq;
      this.fields = applyDesignationFieldState(this.fields, dsgntnSeq);
    }

    const cntrySeq = Number(form.cntrySeq) || 0;
    const stSeq = Number(form.stSeq) || 0;
    const dstrctSeq = Number(form.dstrctSeq) || 0;
    const thslSeq = Number(form.thslSeq) || 0;

    if (cntrySeq !== this.lastCntrySeq) {
      this.lastCntrySeq = cntrySeq;
      this.lastStSeq = 0;
      this.lastDstrctSeq = 0;
      this.lastThslSeq = 0;

      this.loadStates(cntrySeq);
      this.updateFieldOptions('dstrctSeq', []);
      this.updateFieldOptions('thslSeq', []);
      this.updateFieldOptions('citySeq', []);
      return;
    }

    if (stSeq !== this.lastStSeq) {
      this.lastStSeq = stSeq;
      this.lastDstrctSeq = 0;
      this.lastThslSeq = 0;

      this.loadDistricts(stSeq);
      this.updateFieldOptions('thslSeq', []);
      this.updateFieldOptions('citySeq', []);
      return;
    }

    if (dstrctSeq !== this.lastDstrctSeq) {
      this.lastDstrctSeq = dstrctSeq;
      this.lastThslSeq = 0;

      this.loadTehsils(dstrctSeq);
      this.updateFieldOptions('citySeq', []);
      return;
    }

    if (thslSeq !== this.lastThslSeq) {
      this.lastThslSeq = thslSeq;
      this.loadCities(thslSeq);
    }
  }
  // ================= POPUP =================
  openPopup(data?: any, viewOnly = false): void {
    // Full page view mode mein sirf View popup allow hoga.
    if (this.isViewMode && !viewOnly) {
      return;
    }

    this.isPopupViewMode = viewOnly;

    if (data) {
      this.isEditMode = !viewOnly;

      const isCnicLifetime = this.isLifetimeCnicDate(
        data.cncExpryDt,
      );

      this.formData = {
        prsnSeq: data.prsnSeq ?? null,
        flNm: data.flNm ?? '',
        fthrHsbndNm: data.fthrHsbndNm ?? '',
        cnc: data.cnc ?? '',
        cncIssuDt: data.cncIssuDt ?? null,
        isCnicLifetime,
        cncExpryDt: isCnicLifetime
          ? null
          : data.cncExpryDt ?? null,
        psprt: data.psprt ?? '',
        psprtIssuDt: data.psprtIssuDt ?? null,
        psprtExpryDt: data.psprtExpryDt ?? null,
        dob: data.dob ?? null,
        ntnlty: data.ntnlty ?? 0,
        dptNm: data.dptNm ?? 0,
        hod: data.hod ?? '',
        dsgntn: data.dsgntn ?? 0,
        qlfctn: data.qlfctn ?? 0,
        crntPrfssnExp: data.crntPrfssnExp ?? '',
        shrhldng: null,
        rsdntlAddr: data.rsdntlAddr ?? '',
        cntrySeq: data.cntrySeq ?? 0,
        stSeq: data.stSeq ?? 0,
        dstrctSeq: data.dstrctSeq ?? 0,
        thslSeq: data.thslSeq ?? 0,
        citySeq: data.citySeq ?? 0,
        cnicPassportDoc: data.cnicPassportDoc ?? null,
      };

      this.lastCntrySeq = Number(data.cntrySeq) || 0;
      this.lastStSeq = Number(data.stSeq) || 0;
      this.lastDstrctSeq = Number(data.dstrctSeq) || 0;
      this.lastThslSeq = Number(data.thslSeq) || 0;
      this.lastNtnltySeq = Number(data.ntnlty) || 0;

      this.updateCnicExpiryFieldState(isCnicLifetime);
      this.updateNationalityFieldState(this.isPakistanNationality(data.ntnlty));
      this.lastDsgntnSeq = Number(data.dsgntn) || 0;
      this.fields = applyDesignationFieldState(this.fields, data.dsgntn);
      this.preloadGeoForEdit(data);
      this.loadCnicPassportDoc(Number(data.prsnSeq));
    } else {
      this.resetKeyManagementForm();
    }

    this.showPopup = true;
  }

  // New helper — sequential loading so each level resolves cleanly
  private preloadGeoForEdit(data: any) {
    if (data.cntrySeq) {
      this.setup.getStates(data.cntrySeq).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          this.updateFieldOptions(
            'stSeq',
            list.map((s: any) => ({
              label: s.stNm ?? s.name,
              value: s.stSeq ?? s.id,
            })),
          );
        },
      });
    }

    if (data.stSeq) {
      this.setup.getDistricts(data.stSeq).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          this.updateFieldOptions(
            'dstrctSeq',
            list.map((d: any) => ({
              label: d.dstrctNm ?? d.name,
              value: d.dstrctSeq ?? d.id,
            })),
          );
        },
      });
    }

    if (data.dstrctSeq) {
      this.setup.getTehsils(data.dstrctSeq).subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          this.updateFieldOptions(
            'thslSeq',
            list.map((t: any) => ({
              label: t.thslNm ?? t.name,
              value: t.thslSeq ?? t.id,
            })),
          );
        },
      });
    }

    if (data.thslSeq) {
      this.setup.getCity().subscribe({
        next: (res: any) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          this.updateFieldOptions(
            'citySeq',
            list.map((c: any) => ({
              label: c.ctyNm ?? c.cityNm ?? c.name,
              value: c.citySeq ?? c.cityId,
            })),
          );
        },
      });
    }
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    if (this.isViewMode || this.isPopupViewMode) {
      return;
    }

    if (!this.crptOnbrdngSeq || !this.clntSeq) {
      this.toastr.error(this.translate.instant('ONBOARDING.ONBOARDING_OR_CLIENT_ID_MISSING'));
      return;
    }

    const isCnicLifetime = Boolean(
      form.isCnicLifetime ??
      this.formData.isCnicLifetime,
    );

    const cncExpryDt = isCnicLifetime
      ? this.LIFETIME_CNIC_DATE
      : (form.cncExpryDt ?? this.formData.cncExpryDt ?? null);

    const formData = new FormData();

    if (form.prsnSeq) {
      formData.append('prsnSeq', String(Number(form.prsnSeq)));
    }

    formData.append('crptOnbrdngSeq', String(this.crptOnbrdngSeq));
    formData.append('clntSeq', String(this.clntSeq));
    formData.append('flNm', form.flNm ?? '');
    formData.append('fthrHsbndNm', form.fthrHsbndNm ?? '');

    if (form.cnc) {
      formData.append('cnc', String(Number(form.cnc)));
    }

    formData.append('cncIssuDt', form.cncIssuDt ?? '');
    formData.append('cncExpryDt', cncExpryDt ?? '');
    formData.append('psprt', form.psprt && form.psprt.trim() ? form.psprt.trim() : '');
    formData.append('psprtIssuDt', form.psprtIssuDt ?? '');
    formData.append('psprtExpryDt', form.psprtExpryDt ?? '');
    formData.append('prsnTyp', String(this.KEY_MANAGEMENT_PERSON_TYPE));
    formData.append('dob', form.dob ?? '');
    formData.append('ntnlty', String(Number(form.ntnlty) || 0));
    formData.append('dptNm', String(Number(form.dptNm) || 0));
    formData.append('hod', form.hod ?? '');
    formData.append('dsgntn', String(Number(form.dsgntn) || 0));
    formData.append('qlfctn', String(Number(form.qlfctn) || 0));
    formData.append('crntPrfssnExp', form.crntPrfssnExp ?? '');
    formData.append('rsdntlAddr', form.rsdntlAddr ?? '');
    formData.append('cntrySeq', String(Number(form.cntrySeq) || 0));
    formData.append('stSeq', String(Number(form.stSeq) || 0));
    formData.append('dstrctSeq', String(Number(form.dstrctSeq) || 0));
    formData.append('thslSeq', String(Number(form.thslSeq) || 0));
    formData.append('citySeq', String(Number(form.citySeq) || 0));
    formData.append('delFlg', '0');

    const docFile = this.dataUrlToFile(form.cnicPassportDoc, 'cnic-passport');

    if (docFile) {
      formData.append('docs[0].docTyp', String(this.CNIC_PASSPORT_DOC_TYP));
      formData.append('docs[0].delFlg', 'false');
      formData.append('docs[0].docImg', docFile);
    }

    return this.onboarding.saveCorporatePerson(formData).subscribe({
      next: (res: any) => {
        if (res?.__hasError) return;

        const message = this.translate.instant(this.isEditMode ? 'ONB_KEY_MGMT.KEY_MANAGEMENT_UPDATED' : 'ONB_KEY_MGMT.KEY_MANAGEMENT_ADDED');

        this.toastr.success(message);

        this.showPopup = false;
        this.resetKeyManagementForm();
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONBOARDING.SAVE_FAILED');
        this.toastr.error(msg);
      },
    });
  };

  // ================= DELETE =================
  deleteKeyManagement(row: any) {
    if (this.isViewMode) return;

    this.keyManagementToDelete = row;
    this.deleteMessage = this.translate.instant('ONB_KEY_MGMT.DELETE_KEY_MANAGEMENT_CONFIRM', { name: row.flNm });
    this.showDeletePopup = true;
  }

  confirmDeleteKeyManagement(): void {
    if (!this.keyManagementToDelete) return;

    const row = this.keyManagementToDelete;

    const payload = new FormData();

    if (row.prsnSeq) {
      payload.append('prsnSeq', String(Number(row.prsnSeq)));
    }

    payload.append('crptOnbrdngSeq', String(this.crptOnbrdngSeq));
    payload.append('clntSeq', String(this.clntSeq));
    payload.append('flNm', row.flNm ?? '');
    payload.append('fthrHsbndNm', row.fthrHsbndNm ?? '');
    if (row.cnc) {
      payload.append('cnc', String(Number(row.cnc)));
    }

    payload.append('cncIssuDt', row.cncIssuDt ?? '');
    payload.append('cncExpryDt', row.cncExpryDt ?? '');
    payload.append('psprt', row.psprt && row.psprt.trim() ? row.psprt.trim() : '');
    payload.append('psprtIssuDt', row.psprtIssuDt ?? '');
    payload.append('psprtExpryDt', row.psprtExpryDt ?? '');
    payload.append('prsnTyp', String(this.KEY_MANAGEMENT_PERSON_TYPE));
    payload.append('dob', row.dob ?? '');
    payload.append('ntnlty', String(Number(row.ntnlty) || 0));
    payload.append('dptNm', String(Number(row.dptNm) || 0));
    payload.append('hod', row.hod ?? '');
    payload.append('dsgntn', String(Number(row.dsgntn) || 0));
    payload.append('qlfctn', String(Number(row.qlfctn) || 0));
    payload.append('crntPrfssnExp', row.crntPrfssnExp ?? '');
    payload.append('rsdntlAddr', row.rsdntlAddr ?? '');
    payload.append('cntrySeq', String(Number(row.cntrySeq) || 0));
    payload.append('stSeq', String(Number(row.stSeq) || 0));
    payload.append('dstrctSeq', String(Number(row.dstrctSeq) || 0));
    payload.append('thslSeq', String(Number(row.thslSeq) || 0));
    payload.append('citySeq', String(Number(row.citySeq) || 0));
    payload.append('delFlg', '1');

    this.onboarding.saveCorporatePerson(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('ONB_KEY_MGMT.KEY_MANAGEMENT_DELETED'));
        this.keyManagementToDelete = null;
        this.loadData();
      },
      error: (err) => {
        const msg = err?.error?.error || err?.error?.message || this.translate.instant('ONBOARDING.DELETE_FAILED');
        this.toastr.error(msg);
      },
    });
  }

  goToNext() {
    this.router.navigate(['/onboarding/committee-members', this.crptOnbrdngSeq], {
      queryParams: {
        clientSeq: this.clntSeq,
        ...(this.isViewMode ? { mode: 'view' } : {}),
      },
    });
  }

  // ================= ACTIONS =================

  actions: TableAction[] = [];

  buildActions(): void {
    const viewAction: TableAction = {
      icon: this.viewIcon,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    };

    if (this.isViewMode) {
      this.actions = [viewAction];
      return;
    }

    this.actions = [
      viewAction,
      {
        icon: this.icons.edit,
        tooltip: 'COMMON.EDIT',
        class: 'action-edit',
        onClick: (row) => this.openPopup(row, false),
      },
      {
        icon: this.icons.delete ?? this.icons.edit,
        tooltip: 'COMMON.DELETE',
        class: 'action-delete',
        onClick: (row) => this.deleteKeyManagement(row),
      },
    ];
  }
  private resetKeyManagementForm(): void {
    this.isEditMode = false;
    this.isPopupViewMode = false;

    this.formData = {
      prsnSeq: null,
      flNm: '',
      fthrHsbndNm: '',
      cnc: '',
      cncIssuDt: null,
      isCnicLifetime: false,
      cncExpryDt: null,
      psprt: '',
      psprtIssuDt: null,
      psprtExpryDt: null,
      dob: null,
      ntnlty: null,
      dptNm: null,
      hod: '',
      dsgntn: null,
      qlfctn: null,
      crntPrfssnExp: '',
      shrhldng: null,
      rsdntlAddr: '',
      cntrySeq: null,
      stSeq: null,
      dstrctSeq: null,
      thslSeq: null,
      citySeq: null,
      cnicPassportDoc: null,
    };

    this.lastCntrySeq = 0;
    this.lastStSeq = 0;
    this.lastDstrctSeq = 0;
    this.lastThslSeq = 0;
    this.lastNtnltySeq = 0;

    this.updateCnicExpiryFieldState(false);
    this.updateNationalityFieldState(true);
    this.lastDsgntnSeq = 0;
    this.fields = applyDesignationFieldState(this.fields, null);

    this.updateFieldOptions('stSeq', []);
    this.updateFieldOptions('dstrctSeq', []);
    this.updateFieldOptions('thslSeq', []);
    this.updateFieldOptions('citySeq', []);

    // Dynamic form ko new references milengi
    this.formData = { ...this.formData };
    this.fields = [...this.fields];
  }
}
